"use client";

import { useAuth } from "@clerk/clerk-react";
import { useCallback } from "react";
import { fetchFromBackend } from "@/lib/utils/api";

/**
 * Hook for making authenticated API calls to the backend
 * Automatically includes Clerk JWT token in requests
 */
export function useAuthenticatedFetch() {
  const { getToken } = useAuth();

  const authenticatedFetch = useCallback(
    async <T = unknown>(
      endpoint: string,
      options: RequestInit = {}
    ): Promise<T> => {
      const token = await getToken();

      if (!token) {
        throw new Error("Not authenticated");
      }

      return fetchFromBackend<T>(endpoint, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [getToken]
  );
  return { authenticatedFetch };
}

/**
 * Example usage:
 *
 * const { authenticatedFetch } = useAuthenticatedFetch();
 *
 * // GET request
 * const data = await authenticatedFetch('/example/profile');
 *
 * // POST request
 * const result = await authenticatedFetch('/example/create', {
 *   method: 'POST',
 *   body: JSON.stringify({ name: 'test' }),
 * });
 */
